import { IDictionary } from 'util/domain/standard-types';
import { FolderDetails } from 'sharebase-app/components/folderDetails/folderDetails';
import { CalendarHeatMapDay } from 'sharebase/components/calendarHeatMap/calendarHeatMap'
import { DocumentDetails } from 'sharebase-app/components/documentDetails/documentDetails';
import { ActivityToken } from 'sharebase/domain';
import { StaticToggleService } from 'util/services/staticToggleService';
import { EnvironmentService } from 'phoenix/services/environmentService';

require('./activityLog.styles');
const template = require('./activityLog.template');

export enum ActivityIcons {
	'viewed'= 'visibility',
	'created'= 'file_upload',
	'downloaded'= 'file_download',
	'shared'= 'person_add',
	'commented'= 'chat',
	'linked'= 'link',
	'revoked'= 'not_interested',
	'revokedlink'= 'not_interested',
	'edited'= 'edit',
	'added'= 'add',
	'deleted'= 'delete',
	'renamed'= 'text_format',
	'restored'= 'history',
	'emailedlink'= 'email',
	'emailedshare'= 'email',
	'moved'= 'open_with',
	'moved folder'= 'open_with',
	'purged'= 'delete_forever',
	'permanently deleted'= 'delete_forever',
	'locked'= 'lock',
	'unlocked'= 'lock_open',
	'default'= 'info_outline',
	'taskCreated' = 'assignment_turned_in',
	'taskApproved' = 'assignment_turned_in',
	'taskRejected' = 'assignment_turned_in',
	'taskCancelled' = 'assignment_turned_in',
	'taskDeleted' = 'assignment_turned_in'
}

export interface ItemActivity{
	activityDate:Date,
	conciseMessage:string,
	details:any,
	message:string,
	tokens:any[],
	userName:string
}
export interface MostRecentActivity{
	activityTime:Date,
	user:string
}
export interface DailyActivityItems{
	activities:any,
	count:number,
	date:Date
}
export interface GroupedActivityItems{
	activities:ItemActivity[],
	count:number,
	label:string,
	mostRecent:MostRecentActivity,
	icon:ActivityIcons;
}

export function ActivityLogDirective($mdMedia):ng.IDirective {
	return {
		restrict: 'E',
		controller: ActivityLogController,
		controllerAs: 'vm',
		template,
		bindToController: true,
		scope: {
			loadingActivities: '=',
			details: '=',
			type: '@',
			id: '=?'
		},
		link: (scope, element, attrs, vm:ActivityLogController)=>{
			if(vm.staticToggleService.isEnabled('activity-calendar-view')){ 
				scope.$watchCollection('vm.details.activities', (newVal:ItemActivity[])=>{
					if(!newVal){return;}

					vm.groupActivities(newVal);				
				});

				scope.$watch('vm.activityDate', (newVal)=>{
					if(!newVal){return;}

					vm.selectedDailyActivities = null;
					vm.setSelectedDailyActivites();

					if($mdMedia('sm') || $mdMedia('xs')){
						vm.showCalendar = false;
					}
				});
			}
		}
	}
}

ActivityLogDirective.$inject = [
	'$mdMedia'
];

export class ActivityLogController {
	public static $inject = [
		'$locale',
		'$state',
		'pxTokenKeyValueFilter',
		'staticToggleService',
		'pxEnvironmentService',
		'gettextCatalog'
	];

	constructor(
		private $locale:ng.ILocaleService,
		private $state,
		private tokenFilter,
		public staticToggleService:StaticToggleService,
		private pxEnvironmentService:EnvironmentService,
		private gettextCatalog:ng.gettext.gettextCatalog
	) {	
		this.calendarView = this.staticToggleService.isEnabled('activity-calendar-view');
	}

	public activityDate: Date;
	public heatMapDays: CalendarHeatMapDay[];
	public fullGroupedActivities: any[];
	public loadingActivities:boolean = this.loadingActivities;
	public details:FolderDetails|DocumentDetails = this.details;
	public type:string;
	public id:number = +this.id || null;

	public calendarView: boolean = false;
	public showCalendar: boolean = false;
	public groupedActivities: any;	
	public dailyActivities: any;
	public selectedDailyActivities: any;
	public typeActivities: any = {};
	public firstActivity: Date;
	public lastActivity: Date;
	public activityIcons = ActivityIcons;

	private taskCreatedStr = this.gettextCatalog.getString('Task Created');
	private taskCancelledStr = this.gettextCatalog.getString('Task Cancelled');
	private taskResolvedStr = this.gettextCatalog.getString('Task Resolved');

	private actionLabels = {
		// encompasses all task @Action tokens from API
		'taskCreated': this.taskCreatedStr,
		'taskCancelled': this.taskCancelledStr,
		'taskDeleted': this.taskCancelledStr,
		'taskApproved': this.taskResolvedStr,
		'taskDenied': this.taskResolvedStr,
		'taskRejected': this.taskResolvedStr,
		'taskexpired': this.taskResolvedStr

	}
	
	public getActivityIcon(action:string) {
		return this.activityIcons[action] || this.activityIcons['default'];
	}

	public shouldShowLink(token:ActivityToken) {
		if (
			this.type !== 'folder' ||
			token.tuple.value === '(null)' ||
			(token.tuple.key === 'Folder' && this.id === +token.tuple.value)
		) {
			return false;
		}
		else {
			return true;
		}
	}

	public getFullMessage(tokens:ActivityToken[]) {
		let message = ``;
		tokens.forEach(token => message += `${token.phrase} `);
		return message;
	}

	public craftLink(token:ActivityToken) {
		let currentState:string = this.$state.includes('content.shared') ? 'content.shared' : 'content.myFiles';
		let link:string;
		let config = {
			absolute: false
		};

		if (token.tuple && this.type !== 'document') {
			if (token.tuple.key === 'Document') {
				link = this.$state.href(`${currentState}.document`, { 'docID': token.tuple.value }, config);
			}
			else if (token.tuple.key === 'Folder' && token.tuple.value != this.id.toString()) {
				link = this.$state.href(`${currentState}.folder`, { 'folderID': token.tuple.value }, config);
			}
		}
		return link;
	}

	// 2018-02-16T17:22:57.347Z => Friday, February 16th, 2018 12:22:57 PM 
	// 							   Fri, Feb 12, 2018
	public formatDate(date:string, concise:boolean = false) {
		let dateTimeOptions:Intl.DateTimeFormatOptions = {
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric',
			hour: 'numeric',
			minute: 'numeric',
			second: 'numeric',
			timeZone: this.pxEnvironmentService.timeZone
		};

		if (concise) {
			dateTimeOptions = {
				weekday: 'short',
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				timeZone: this.pxEnvironmentService.timeZone
			};
		}

		let activityFormat = new Intl.DateTimeFormat(this.$locale.id, dateTimeOptions);
		
		return activityFormat.format(new Date(date));
	}
	
    public groupActivities(activities:ItemActivity[]){
		let grouped: any = {};
		let daily: any = {};
        activities.forEach(a=>{
			let actionKey = this.tokenFilter(a.conciseMessage, 'Action')[0].key;			
			let actionValue = this.tokenFilter(a.conciseMessage, 'Action')[0].value;			
            let user = this.tokenFilter(a.conciseMessage, 'User')[0].value;
            let activityTime = a.activityDate;
            let activitDate = activityTime.toDateString();
            let propDate = new Date(activitDate);
            let prop = this.generateGroupedDayProp(propDate);
			let label = this.getActionLabel(actionKey, actionValue);
			
            if(!daily[prop]){
                daily[prop] = <DailyActivityItems>{
                    date: propDate,
                    count: 0,
                    activities:{}
                };
			}
			if(!grouped[label]){			
                grouped[label] = <GroupedActivityItems>{
					label,
					mostRecent:<MostRecentActivity>{
						user,
						activityTime
					},
					activities:[],
					count:0,
					icon:ActivityIcons[actionKey]
                };
            }

            if(!daily[prop]['activities'][label]){
                daily[prop]['activities'][label] = {
					label,
					mostRecent:{
						user,
						activityTime
					},
					activities:[],
					count:0,
					icon: ActivityIcons[actionKey]
                };
            }

            daily[prop]['activities'][label].activities.push(a);
			daily[prop].count++;	
			daily[prop]['activities'][label].count++;		
			
            grouped[label].activities.push(a);
			grouped[label].count++;	
			
			//create calendar heat map array
			this.heatMapDays = Object.keys(daily).map(d=>{ return {date:daily[d].date, count: daily[d].count};});

        });

		this.groupedActivities = angular.copy(grouped);
		this.dailyActivities = angular.copy(daily);

    }

	public setSelectedDailyActivites(){
		if(!this.activityDate){ return;}

		let selectedProp = this.generateGroupedDayProp(this.activityDate);
		if(this.dailyActivities[selectedProp]){
			this.selectedDailyActivities = angular.copy(this.dailyActivities[selectedProp].activities);
		}
		
	}

	private generateGroupedDayProp(date){
		return date.getFullYear().toString() + this.getDateNumber(date.getMonth().toString()) + this.getDateNumber(date.getDate());
	}

    private getDateNumber(number){
        return number < 10 ? '0' + number : number.toString();
	}

	private getActionLabel(actionKey: string, actionValue: string): string {
		if (actionKey in this.actionLabels) {
			return this.actionLabels[actionKey];
		} else {
			return actionValue.charAt(0).toUpperCase() + actionValue.substr(1).toLowerCase();
		}
	}

	
}


// WEBPACK FOOTER //
// ./src/sharebase-app/components/activityLog/activityLog.ts